
import React from 'react';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { useInventaire } from '@/hooks/useInventaire';
import { useEspacesStockage } from '@/hooks/useEspacesStockage';

interface FormulaireModificationArticleProps {
  article: any;
  onClose: () => void;
  onSuccess?: () => void;
}

const formSchema = z.object({
  nom: z.string().min(2, {
    message: "Le nom doit comporter au moins 2 caractères.",
  }),
  description: z.string().optional(),
  quantite: z.number().min(0, {
    message: "La quantité doit être au moins 0.",
  }),
  quantite_max: z.number().min(1, {
    message: "La quantité maximum doit être au moins 1.",
  }),
  categorie: z.string().optional(),
  sous_categorie: z.string().optional(),
  unite: z.string().optional(),
  prix_unitaire: z.number().optional(),
  date_expiration: z.string().optional(),
  espace_stockage_id: z.string().optional(),
});

const FormulaireModificationArticle = ({ article, onClose, onSuccess }: FormulaireModificationArticleProps) => {
  const { modifierArticle, refetchArticles, categories, sousCategories } = useInventaire();
  const { espaces } = useEspacesStockage();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nom: article.nom || "",
      description: article.description || "",
      quantite: article.quantite || 0,
      quantite_max: article.quantite_max || 1,
      categorie: article.categorie || "",
      sous_categorie: article.sous_categorie || "",
      unite: article.unite || "unité",
      prix_unitaire: article.prix_unitaire || 0,
      date_expiration: article.date_expiration || "",
      espace_stockage_id: article.espace_stockage_id || "",
    },
  });

  const selectedCategorie = form.watch("categorie");
  const sousCategorieFiltrees = sousCategories.filter(sc => 
    sc.categorie_id === categories.find(c => c.nom === selectedCategorie)?.id
  );

  // Filtrer les espaces de stockage pour le bateau de l'article
  const espacesArticle = espaces.filter(e => e.bateau_id === article.bateau_id);

  const handleSubmit = async (data: any) => {
    console.log('=== DÉBUT MODIFICATION ARTICLE ===');
    console.log('Données du formulaire avant traitement:', data);
    
    try {
      // Nettoyer les données pour convertir les chaînes vides en null
      const cleanedData = {
        nom: data.nom,
        description: data.description || null,
        quantite: data.quantite,
        quantite_max: data.quantite_max,
        categorie: data.categorie || null,
        sous_categorie: data.sous_categorie === "none" || !data.sous_categorie ? null : data.sous_categorie,
        unite: data.unite || "unité",
        prix_unitaire: data.prix_unitaire || null,
        date_peremption: data.date_expiration || null, // Note: utilise date_peremption dans la DB
        espace_stockage_id: data.espace_stockage_id === "none" || !data.espace_stockage_id ? null : data.espace_stockage_id,
      };
      
      console.log('Données nettoyées envoyées à la DB:', cleanedData);
      console.log('ID de l\'article à modifier:', article.id);
      
      const result = await modifierArticle(article.id, cleanedData);
      console.log('Résultat de la modification:', result);
      
      if (result?.error) {
        console.error('Erreur retournée par modifierArticle:', result.error);
        throw result.error;
      }
      
      console.log('Modification réussie, refresh des articles...');
      await refetchArticles();
      console.log('Refresh terminé');
      
      onSuccess?.();
      onClose();
      toast({
        title: "Succès",
        description: "L'article a été modifié avec succès",
      });
      console.log('=== FIN MODIFICATION ARTICLE ===');
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      toast({
        title: "Erreur",
        description: "Impossible de modifier l'article",
        variant: "destructive",
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="nom"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nom</FormLabel>
              <FormControl>
                <Input placeholder="Nom de l'article" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Input placeholder="Description de l'article" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="quantite"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Quantité actuelle</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="Quantité"
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="quantite_max"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Quantité maximum</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="Quantité max"
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="categorie"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Catégorie</FormLabel>
                <Select value={field.value || ""} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Choisir une catégorie" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.nom}>
                        {cat.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="sous_categorie"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Sous-catégorie</FormLabel>
                <Select value={field.value || "none"} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Choisir une sous-catégorie" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">Aucune sous-catégorie</SelectItem>
                    {sousCategorieFiltrees.map(sc => (
                      <SelectItem key={sc.id} value={sc.nom}>
                        {sc.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="unite"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Unité de mesure</FormLabel>
                <Select value={field.value || "unité"} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Choisir une unité" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="unité">unité</SelectItem>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="g">g</SelectItem>
                    <SelectItem value="L">L</SelectItem>
                    <SelectItem value="mL">mL</SelectItem>
                    <SelectItem value="boîte">boîte</SelectItem>
                    <SelectItem value="pack">pack</SelectItem>
                    <SelectItem value="sachet">sachet</SelectItem>
                    <SelectItem value="bouteille">bouteille</SelectItem>
                    <SelectItem value="pot">pot</SelectItem>
                    <SelectItem value="tube">tube</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="espace_stockage_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Espace de stockage</FormLabel>
                <Select value={field.value || "none"} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Choisir un espace" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">Aucun espace spécifique</SelectItem>
                    {espacesArticle.map(espace => (
                      <SelectItem key={espace.id} value={espace.id}>
                        {espace.nom} ({espace.type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <FormField
          control={form.control}
          name="prix_unitaire"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Prix unitaire (€)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Prix unitaire"
                  {...field}
                  onChange={(e) => field.onChange(Number(e.target.value) || 0)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="date_expiration"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Date d'expiration</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit">
            Modifier
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default FormulaireModificationArticle;
